import { Link } from 'react-router-dom'
import BigButton from '../components/BigButton'
import { eventDateSentence, useSeason } from '../lib/season'

// Publieke "stuur-deze-link"-pagina met alles voor op de dag zelf. Bewust
// losgetrokken van /reglement: dat is de juridische tekst, dit is de
// mensentaal-versie. Wat hier staat mag het reglement nooit tegenspreken.
export default function Praktisch() {
  // De marktdag komt uit /beheer in plaats van hier hardgecodeerd te staan,
  // zodat deze pagina niet met september van vorig jaar blijft staan zodra
  // het seizoen in mei vanzelf heropent. Nog niet ingevuld = geen datum in
  // de kop, geen verkeerde.
  const { season } = useSeason()
  const datum = eventDateSentence(season)
  return (
    <>
      {/* Hero */}
      <section className="bg-lime border-b-[3px] border-ink">
        <div className="mx-auto max-w-5xl px-5 pt-10 pb-12">
          <p className="font-display text-xs sm:text-sm tracking-display uppercase text-ink/80">
            Rommelmarkt{datum ? ` · ${datum}` : ''}
          </p>
          <h1 className="mt-3 font-display text-7xl sm:text-9xl leading-[0.92] tracking-display uppercase">
            Praktisch
          </h1>
          <p className="mt-5 text-lg max-w-2xl">
            Alles wat je moet weten voor op de dag zelf: de uren, hoe groot je plek is, waar je je auto
            kwijt kan en hoe het zit met de betaling. Vind je hier je antwoord niet?{' '}
            <a className="underline" href="mailto:rommelmarkt@fluweelfeest.be">Mail ons gerust.</a>
          </p>
          <nav className="mt-7 flex flex-wrap gap-2" aria-label="Snel naar">
            <Anker naar="uren">De uren</Anker>
            <Anker naar="plek">Je plek</Anker>
            <Anker naar="autos">Auto's</Anker>
            <Anker naar="verkopen">Verkopen</Anker>
            <Anker naar="opruimen">Opruimen</Anker>
            <Anker naar="betaling">Betaling</Anker>
          </nav>
        </div>
      </section>

      {/* De uren */}
      <section id="uren" className="bg-ink text-offwhite border-b-[3px] border-ink scroll-mt-4">
        <div className="mx-auto max-w-5xl px-5 py-14">
          <h2 className="font-display text-5xl sm:text-6xl tracking-display uppercase text-lime mb-3">
            De uren
          </h2>
          <p className="text-offwhite/80 mb-8 max-w-2xl">
            De straat is een hele dag rommelmarkt. Deze drie uren zijn de enige die je echt moet onthouden.
          </p>
          <div className="grid sm:grid-cols-3 border-[3px] border-lime divide-y-[3px] sm:divide-y-0 sm:divide-x-[3px] divide-lime">
            <Uur tijd="07u" titel="Opzetten mag">
              Vanaf 7 uur mag je je stand komen opzetten. Vroeger komen hoeft niet: je plek is van jou.
            </Uur>
            <Uur tijd="09u" titel="Auto's buiten · markt opent">
              Om 9 uur moeten alle auto's de straat uit zijn en gaat de rommelmarkt open voor bezoekers.
            </Uur>
            <Uur tijd="17u" titel="Afbreken">
              Vanaf 5 uur 's namiddags mag je opruimen. Vroeger afbreken mag niet — dan valt de straat stil
              terwijl er nog volk rondloopt.
            </Uur>
          </div>
        </div>
      </section>

      {/* Je plek */}
      <section id="plek" className="bg-offwhite border-b-[3px] border-ink scroll-mt-4">
        <div className="mx-auto max-w-5xl px-5 py-14">
          <h2 className="font-display text-5xl sm:text-6xl tracking-display uppercase mb-8">Je plek</h2>
          <div className="grid sm:grid-cols-2 gap-6">
            <Blok titel="Ongeveer 5 meter">
              <p>
                Eén plaats is <strong>ongeveer 5 lopende meter</strong>. De ene plek is wat breder dan de
                andere: dat hangt af van de gevel en van wat er op straat staat — palen, bomen, fietsrekken,
                een vuilbak.
              </p>
              <p className="mt-3">
                Heb je meer plaats nodig? Dan schrijf je je voor twee plaatsen in, aan €5 per plaats.
              </p>
            </Blok>
            <Blok titel="Waar begint en stopt ze?">
              <p>
                Sta je voor een duidelijke gevel, dan is die gevel je plek: van deur tot deur.
              </p>
              <p className="mt-3">
                Is er geen duidelijke gevel — aan het park, een lange muur, een hoek — dan{' '}
                <strong>markeren wij je plaats op de grond</strong>. Je ziet het dus ter plaatse staan.
              </p>
            </Blok>
            <Blok titel="Blijf binnen je markering" accent>
              <p>
                Ook als je buur er nog niet is. Die komt misschien wat later toe, en anders schuift de hele
                straat door en komt er iemand zonder plek te staan.
              </p>
            </Blok>
            <Blok titel="Welke plek was het weer?">
              <p>
                Je plek staat in je bevestigingsmail, en op je persoonlijke plek-pagina — de link onderaan
                die mail. Daar zie je ze ook op de plattegrond staan.
              </p>
              <p className="mt-3">
                Link kwijt?{' '}
                <a className="underline" href="mailto:rommelmarkt@fluweelfeest.be">Mail ons</a>, we sturen ze
                opnieuw.
              </p>
            </Blok>
          </div>
        </div>
      </section>

      {/* Auto's */}
      <section id="autos" className="bg-red text-offwhite border-b-[3px] border-ink scroll-mt-4">
        <div className="mx-auto max-w-5xl px-5 py-14">
          <h2 className="font-display text-5xl sm:text-6xl tracking-display uppercase mb-3">
            Auto's en parkeren
          </h2>
          <p className="text-offwhite/90 mb-8 max-w-2xl text-lg">
            Dit is het punt waar het elk jaar op vastloopt, dus lees het even.
          </p>
          <ul className="space-y-5 max-w-3xl">
            <Punt titel="Je auto kan niet bij je stand blijven staan.">
              Uitladen mag, en daarna zet je hem weg. Om 9 uur moet de straat auto-vrij zijn.
            </Punt>
            <Punt titel="Parkeren doe je het best aan het Nobelplein.">
              Van daar is het een korte wandeling naar de markt.
            </Punt>
            <Punt titel="Laat je spullen niet alleen achter terwijl je gaat parkeren.">
              Kom met twee, of spreek met je buur af dat die even mee een oogje in het zeil houdt.
            </Punt>
            <Punt titel="Sta je aan het Fluweelpark, langs het voetpad?">
              Daar geraak je niet met de auto tot aan je plek. Kom met kleiner vervoer — een bakfiets of een
              steekkar — of los je spullen via de Werkstraat.
            </Punt>
            <Punt titel="Hou de rijbaan vrij.">
              Minstens 4 meter breed, zodat de brandweer en een ziekenwagen er altijd door kunnen. Hydranten
              en gasafsluiters moeten bereikbaar blijven, en aanhangwagens laat je buiten de marktzone.
            </Punt>
          </ul>
        </div>
      </section>

      {/* Verkopen */}
      <section id="verkopen" className="bg-offwhite border-b-[3px] border-ink scroll-mt-4">
        <div className="mx-auto max-w-5xl px-5 py-14">
          <h2 className="font-display text-5xl sm:text-6xl tracking-display uppercase mb-8">
            Wat verkoop je?
          </h2>
          <div className="grid sm:grid-cols-2 gap-6">
            <Blok titel="Wel">
              <p>
                Tweedehands spullen van thuis. De rommelmarkt is er voor particulieren: je zolder opruimen,
                niet je winkel uitbaten.
              </p>
            </Blok>
            <Blok titel="Niet">
              <p>
                Nieuwe producten en professionele verkoop. En geen eten of drank: dat doet de organisatie —
                zo blijft er iets over voor de kas van het feest.
              </p>
            </Blok>
          </div>
          <div className="mt-6">
            <Blok titel="Elektrische toestellen">
              <p>
                Die mag je verkopen, maar koper en verkoper zijn daar zelf verantwoordelijk voor. De
                organisatie test geen toestellen ter plaatse.
              </p>
            </Blok>
          </div>
        </div>
      </section>

      {/* Opruimen */}
      <section id="opruimen" className="bg-offwhite border-b-[3px] border-ink scroll-mt-4">
        <div className="mx-auto max-w-5xl px-5 pb-14">
          <h2 className="font-display text-5xl sm:text-6xl tracking-display uppercase mb-8">Opruimen</h2>
          <ul className="space-y-5 max-w-3xl">
            <Punt titel="Wat je niet verkocht, gaat mee naar huis." dark>
              Ook de dozen, de kapotte stukken en de tafel die je toch niet meer wou.
            </Punt>
            <Punt titel="Niet in de straatvuilbakken." dark>
              Die zijn er voor de bezoekers. Er een doos naast zetten is sluikstorten, en daar krijgt het
              feest een boete voor.
            </Punt>
            <Punt titel="Laat je plek achter zoals je ze vond." dark>
              Zondagochtend moet de straat er weer uitzien als een straat.
            </Punt>
          </ul>
        </div>
      </section>

      {/* Betaling */}
      <section id="betaling" className="bg-lime border-b-[3px] border-ink scroll-mt-4">
        <div className="mx-auto max-w-5xl px-5 py-14">
          <h2 className="font-display text-5xl sm:text-6xl tracking-display uppercase mb-8">Betaling</h2>
          <div className="grid sm:grid-cols-2 gap-6">
            <Blok titel="€5 per plek" light>
              <p>
                Eén plaats van ongeveer 5 meter kost €5. Je plek is pas definitief als de betaling binnen is.
              </p>
              <p className="mt-3">
                Betalen doe je met de <strong>gestructureerde mededeling</strong> uit je bevestigingsmail, of
                door de QR-code in die mail te scannen met je bank-app. Zo weten we meteen van wie het geld komt.
              </p>
            </Blok>
            <Blok titel="Nog niet betaald?" light accent>
              <p>
                We kijken de betalingen <strong>vrijdagavond</strong> nog een laatste keer na. Staat die van
                jou er dan niet bij, dan komen we <strong>zaterdag cash langs</strong> bij je stand — hou dan
                €5 per plek klaar.
              </p>
              <p className="mt-3">
                Niet zeker of je al betaalde?{' '}
                <a className="underline" href="mailto:rommelmarkt@fluweelfeest.be">Mail ons</a>, dan kijken we
                het voor je na.
              </p>
            </Blok>
          </div>
        </div>
      </section>

      {/* Vragen / CTA */}
      <section className="bg-ink text-lime">
        <div className="mx-auto max-w-5xl px-5 py-12 space-y-6">
          <div className="flex flex-col sm:flex-row gap-4 sm:items-center sm:justify-between">
            <div>
              <p className="font-display text-3xl sm:text-4xl tracking-display uppercase">Nog een vraag?</p>
              <p className="text-offwhite/80 mt-2">
                Mail{' '}
                <a className="underline" href="mailto:rommelmarkt@fluweelfeest.be">
                  rommelmarkt@fluweelfeest.be
                </a>{' '}
                — we antwoorden meestal dezelfde dag.
              </p>
            </div>
            <Link to="/inschrijven">
              <BigButton variant="lime">Schrijf je in</BigButton>
            </Link>
          </div>
          <p className="text-offwhite/70 text-base">
            De volledige spelregels staan in het{' '}
            <Link to="/reglement" className="text-lime underline">
              reglement
            </Link>
            . Wie zich inschrijft, aanvaardt dat reglement.
          </p>
        </div>
      </section>
    </>
  )
}

function Anker({ naar, children }: { naar: string; children: React.ReactNode }) {
  return (
    <a href={`#${naar}`} className="pill hover:bg-ink hover:text-lime transition">
      {children}
    </a>
  )
}

function Uur({ tijd, titel, children }: { tijd: string; titel: string; children: React.ReactNode }) {
  return (
    <div className="p-6">
      <div className="font-display text-6xl tracking-display uppercase text-lime leading-none">{tijd}</div>
      <div className="font-display text-2xl tracking-display uppercase mt-2">{titel}</div>
      <p className="text-offwhite/80 mt-2 text-base">{children}</p>
    </div>
  )
}

function Blok({
  titel,
  children,
  accent = false,
  light = false,
}: {
  titel: string
  children: React.ReactNode
  accent?: boolean
  light?: boolean
}) {
  return (
    <div
      className={`border-[3px] border-ink p-6 ${
        accent ? 'shadow-block ' : ''
      }${light ? 'bg-offwhite' : 'bg-lime'}`}
    >
      <h3 className="font-display text-3xl tracking-display uppercase mb-3">{titel}</h3>
      <div className="text-base leading-relaxed">{children}</div>
    </div>
  )
}

function Punt({
  titel,
  children,
  dark = false,
}: {
  titel: string
  children: React.ReactNode
  dark?: boolean
}) {
  return (
    <li className={`border-l-[6px] pl-4 ${dark ? 'border-ink' : 'border-lime'}`}>
      <p className="font-display text-2xl tracking-display uppercase leading-tight">{titel}</p>
      <p className={`mt-1 text-base ${dark ? 'text-ink/80' : 'text-offwhite/90'}`}>{children}</p>
    </li>
  )
}
